import { Suspense, lazy, useState } from "react";
import { useTextos } from "../context/useTextos";
import DamagePhotoMarker from "./DamagePhotoMarker";
import Vehicle3DErrorBoundary from "./Vehicle3DErrorBoundary";
import { resumoSaudeVeiculo, STATUS_SAUDE_LABELS, STATUS_SAUDE_COLORS } from "../lib/avariaModel";
import { hasWebGL } from "../lib/webglSupport";
import { useSettings } from "../context/useSettings";

// Carregado sob demanda: evita colocar three.js/@react-three no bundle
// principal (carregado só quando este painel é exibido).
const VehicleInspection3D = lazy(() => import("./VehicleInspection3D"));

// Painel de avarias/riscos externos do veículo — combina a inspeção 3D
// interativa (modelo GLTF realista, peças independentes clicáveis) e a
// marcação sobre fotos reais (câmera). Avarias de gravidade crítica contam
// como critério de avaliação do checklist (ver Dashboard, Relatórios e
// export PDF/HTML).
export default function AvariasPanel({ avarias, onChange, readOnly, modeloId }) {
  const { t } = useTextos();
  // Fase 6: indicador automático de saúde (OK/Atenção/Crítico), no lugar do
  // texto simples "N crítica(s)" que existia antes.
  const resumo = resumoSaudeVeiculo(avarias);
  // Fase 7: checagem de suporte a WebGL, calculada uma única vez.
  const [suportaWebgl] = useState(hasWebGL);
  // AUD-A: a inspeção 3D passou a ser opcional por oficina, controlada pelo
  // painel administrativo (ver components/ChecklistConfigGate.js). Padrão
  // desligado -- ver lib/settingsDefaults.js.
  //
  // A exceção do `temDiagrama` existe de propósito: um checklist que JÁ tem
  // marcação no diagrama continua exibindo o bloco mesmo com a chave
  // desligada. Some a ferramenta, nunca a prova -- sem isso a tela
  // esconderia marcações que o PDF (lib/exportChecklist.js) continua
  // imprimindo, e a divergência só apareceria num atendimento real.
  const { settings } = useSettings();
  const temDiagrama = ((avarias && avarias.diagrama) || []).length > 0;
  const mostrar3d = !!(settings.features && settings.features.inspecao3d) || temDiagrama;

  return (
    <section className="form-section">
      <div className="avarias-panel__header">
        <h3>{t("checklist.avarias_titulo")}</h3>
        <span
          className="avarias-panel__status-badge"
          style={{ background: STATUS_SAUDE_COLORS[resumo.status] }}
        >
          {STATUS_SAUDE_LABELS[resumo.status]}
          {resumo.total > 0 && t("checklist.avarias_count", { count: resumo.total })}
        </span>
      </div>
      {/* AUD-C: a dica descreve o que esta REALMENTE na tela. Com a inspecao
          3D desligada, mandar "marque no modelo 3D" fazia o mecanico procurar
          uma ferramenta que nao existe ali. Defeito introduzido no AUD-A. */}
      <p className="hint">
        {readOnly
          ? t("checklist.avarias_hint_readonly")
          : t(mostrar3d ? "checklist.avarias_hint_edit" : "checklist.avarias_hint_edit_sem3d")}
      </p>

      {/* Com mostrar3d=false o bloco inteiro sai da árvore -- e como
          VehicleInspection3D é lazy(), o three.js/@react-three NUNCA chega a
          ser baixado nessa oficina. Desligar deixa o checklist mais leve,
          não só mais limpo. */}
      {mostrar3d && (
        <div className="avarias-panel__block">
          <h4>{t("checklist.avarias_3d_titulo")}</h4>
          {suportaWebgl ? (
            <Vehicle3DErrorBoundary>
              <Suspense fallback={<p className="hint">{t("checklist.avarias_3d_carregando")}</p>}>
                <VehicleInspection3D
                  registros={avarias.diagrama}
                  onChange={(diagrama) => onChange({ ...avarias, diagrama })}
                  readOnly={readOnly}
                  modeloId={modeloId || undefined}
                />
              </Suspense>
            </Vehicle3DErrorBoundary>
          ) : (
            <p className="hint">
              {t("checklist.avarias_3d_sem_webgl")}
            </p>
          )}
        </div>
      )}

      <div className="avarias-panel__block">
        <h4>{t("checklist.avarias_fotos_titulo")}</h4>
        <DamagePhotoMarker
          fotos={avarias.fotos}
          onChange={(fotos) => onChange({ ...avarias, fotos })}
          readOnly={readOnly}
        />
      </div>
    </section>
  );
}
